import { useOrg } from '@/organization/useOrg';
import { useGetSubproject } from '@/queries/useSubprojects';
import { useQueryClient } from '@tanstack/react-query';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useParams } from 'react-router-dom';
import { DecarbFooterBar } from '../components/DecarbFooterBar';
import { DecarbPageLayout } from '../components/DecarbPageLayout';
import { DecarbTabsPanel } from '../components/DecarbTabsPanel';
import { HelperText } from '../components/HelperText';
import { useDecarbTechSelection } from '../hooks';
import { usePencilsDown } from '../hooks/usePencilsDown';
import { usePlugLoadWithMetrics } from '../queries/plugload';
import { safelySaveSubproject } from '../saveHelpers';
import { Tab } from '../types';
import { isPassiveTechnology, storedCategoryCapitalCost } from '../utils.ts';
import { calculateEnergyDemand } from '../utils/calculations';
import { PlugLoadSelectionDetails } from './PlugLoadSelectionDetails';

const TECHNOLOGY_KEY = 'plugload';

export const PlugLoadSelection: React.FC = () => {
  const queryClient = useQueryClient();
  const { subId } = useParams<{ subId: string }>();
  const { selectedOrg } = useOrg();
  const orgId = selectedOrg?.id ?? '';
  const pencilsDown = usePencilsDown();

  const { data: fetchedSubproject } = useGetSubproject(orgId, subId || '');

  const { data: plugLoadData } = usePlugLoadWithMetrics(
    fetchedSubproject?.conventionalAlternative?.buildingParams
      ?.conditionedSpace,
  );

  const {
    costsArePending,
    currentTab,
    selectedItems: selectedItems,
    availableItems: availableItems,
    subproject,
    copyItemSelectionFromTab,
    handleAdd,
    handleChangeItemCount,
    handleRemove,
    setCurrentTab,
  } = useDecarbTechSelection(TECHNOLOGY_KEY, fetchedSubproject, plugLoadData);

  const saveTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const isInitialLoadRef = useRef(true);
  const [isSaving, setIsSaving] = useState(false);

  // Reset initial load flag when fetched data changes
  useEffect(() => {
    isInitialLoadRef.current = true;
  }, [fetchedSubproject]);

  // Debounced auto-save after selection changes
  useEffect(() => {
    if (pencilsDown) return;
    if (!subproject || !subId || !selectedOrg) return;

    if (isInitialLoadRef.current) {
      isInitialLoadRef.current = false;
      return;
    }

    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }

    saveTimeoutRef.current = setTimeout(() => {
      setIsSaving(true);
      safelySaveSubproject(subId, selectedOrg.id, subproject, queryClient)
        .catch(console.error)
        .finally(() => setIsSaving(false));
    }, 500);

    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
    };
  }, [pencilsDown, subproject, subId, selectedOrg, queryClient]);

  const getCostAndEnergyMetrics = useCallback(
    (tab: Tab) => {
      if (!subproject?.[tab]?.technologyParams?.[TECHNOLOGY_KEY]?.params) {
        return undefined;
      }

      const params = subproject[tab].technologyParams[TECHNOLOGY_KEY].params;

      if (params.length === 0 || !plugLoadData || plugLoadData.length === 0) {
        return undefined;
      }

      const mappedItems = params
        .map((param) => {
          const foundItem = plugLoadData.find(
            (item) => String(item.id) === param.techId,
          );
          if (!foundItem) return null;
          return { ...foundItem, count: param.units };
        })
        .filter((item) => item !== null);
      const { energyDemand, systemCoverageArea } = mappedItems.reduce(
        (acc, item) => {
          // Passive technologies have 0% coverage
          const isPassive = isPassiveTechnology(item);

          return {
            energyDemand:
              acc.energyDemand +
              calculateEnergyDemand(item.energyUsePerYr, item.count),
            systemCoverageArea:
              acc.systemCoverageArea +
              (isPassive ? 0 : item.count / Math.max(1, item.limit)),
          };
        },
        {
          energyDemand: 0,
          systemCoverageArea: 0,
        },
      );

      // Capital comes from the stored category total, never local arithmetic --
      // see storedCategoryCapitalCost for why.
      const cost = storedCategoryCapitalCost(
        subproject[tab],
        TECHNOLOGY_KEY,
        costsArePending,
      );

      if (!subproject?.[tab].buildingParams.conditionedSpace) {
        return { cost, energyDemand, systemCoveragePercentage: 0 };
      }

      return {
        cost,
        energyDemand,
        systemCoveragePercentage: systemCoverageArea,
      };
    },
    [subproject, plugLoadData, costsArePending],
  );

  const conventionalMetrics = useMemo(
    () => getCostAndEnergyMetrics('conventionalAlternative'),
    [getCostAndEnergyMetrics],
  );
  const reducedCarbonMetrics = useMemo(
    () => getCostAndEnergyMetrics('reducedCarbonAlternative'),
    [getCostAndEnergyMetrics],
  );
  const zeroCarbonMetrics = useMemo(
    () => getCostAndEnergyMetrics('zeroCarbonAlternative'),
    [getCostAndEnergyMetrics],
  );

  return (
    <DecarbPageLayout
      title='PLUG LOAD'
      tabs={
        <DecarbTabsPanel
          currentTab={currentTab}
          onClick={setCurrentTab}
          conventionalMetrics={conventionalMetrics}
          reducedCarbonMetrics={reducedCarbonMetrics}
          zeroCarbonMetrics={zeroCarbonMetrics}
        />
      }
      footer={
        <DecarbFooterBar
          currentTab={currentTab}
          changeTab={setCurrentTab}
          saveSubproject={(onSuccessFn: () => void) => {
            if (pencilsDown) {
              onSuccessFn();
              return;
            }
            if (subproject && subId && selectedOrg) {
              safelySaveSubproject(
                subId,
                selectedOrg.id,
                subproject,
                queryClient,
              )
                .then(() => {
                  onSuccessFn();
                })
                .catch((error) => {
                  console.error('Failed to save plugload data:', error);
                  alert('Failed to save changes. Please try again.');
                });
            }
          }}
        />
      }
    >
      <PlugLoadSelectionDetails
        availableItems={availableItems}
        selectedItems={selectedItems}
        costEnergyAndCoverageMetrics={getCostAndEnergyMetrics(currentTab)}
        isLoading={isSaving}
        onAdd={handleAdd}
        onRemove={handleRemove}
        onChangeCount={handleChangeItemCount}
        readOnly={pencilsDown}
        helperTextComponent={
          pencilsDown ? undefined : (
            <HelperText
              tab={currentTab}
              onCopySelection={() => copyItemSelectionFromTab(currentTab)}
            />
          )
        }
      />
    </DecarbPageLayout>
  );
};
